// Light leaf: enum + plain object maps for project detail tabs. Kept free of
// icon imports so the api/project barrel (re-exported from here) doesn't drag
// `@fortawesome/react-fontawesome` + dozens of `pro-regular-svg-icons` into
// the app shell. Heavy assets (projectTabIconMap, PROJECT_TABS,
// createFontAwesomeIcon) still live in tabs.tsx — consumers that need them
// deep-path `'api/project/tabs'`.
import type { ProjectId } from 'api/models';
import type { ClassNameOnlyProps } from 'components/ozone-component';

export enum ProjectDetailTabKeys {
  AskAi = 'AskAi',
  Overview = 'Overview',
  FinancialsContainer = 'FinancialsContainer',
  Financials = 'Financials',
  Plan = 'Plan',
  SignalsV2 = 'SignalsV2',
  MeetingPages = 'MeetingPages',
  EmailPages = 'EmailPages',
  Spaces = 'Spaces',
  NitroPages = 'NitroPages',
  Chat = 'Chat',
  TaskList = 'TaskList',
  TaskBoard = 'TaskBoard',
  StatusUpdate = 'StatusUpdate',
  FormResponses = 'FormResponses',
  Timesheet = 'Timesheet',
  Files = 'Files',
  ResourceManagement = 'ResourceManagement',
  ResourceRequest = 'ResourceRequest',
  ResourcePlan = 'ResourcePlan',
  CustomerPortalConfiguration = 'CustomerPortalConfiguration',
  Sheets = 'Sheets',
  Invoices = 'Invoices',
  Expense = 'Expense',
  ExpenseReport = 'ExpenseReport',
}

export interface ProjectDetailTabPropTypes extends ClassNameOnlyProps {
  projectId: ProjectId;
  activeTabKey?: ProjectDetailTabKeys | `mp-${string}` | `lds-${string}`;
}

export const tabApiEnumMap = {
  [ProjectDetailTabKeys.AskAi]: 'askAi',
  [ProjectDetailTabKeys.Overview]: 'overview',
  [ProjectDetailTabKeys.FinancialsContainer]: 'financialsContainer',
  [ProjectDetailTabKeys.Financials]: 'financials',
  [ProjectDetailTabKeys.SignalsV2]: 'signalsV2',
  [ProjectDetailTabKeys.MeetingPages]: 'meetingPages',
  [ProjectDetailTabKeys.EmailPages]: 'emailPages',
  [ProjectDetailTabKeys.Plan]: 'projectPlan',
  [ProjectDetailTabKeys.Spaces]: 'spaces',
  [ProjectDetailTabKeys.NitroPages]: 'nitroPages',
  [ProjectDetailTabKeys.Chat]: 'chat',
  [ProjectDetailTabKeys.StatusUpdate]: 'statusUpdates',
  [ProjectDetailTabKeys.TaskList]: 'list',
  [ProjectDetailTabKeys.TaskBoard]: 'board',
  [ProjectDetailTabKeys.FormResponses]: 'form',
  [ProjectDetailTabKeys.Timesheet]: 'timesheet',
  [ProjectDetailTabKeys.Files]: 'files',
  [ProjectDetailTabKeys.ResourceManagement]: 'resourceManagement',
  [ProjectDetailTabKeys.ResourceRequest]: 'resourceRequest',
  [ProjectDetailTabKeys.ResourcePlan]: 'resourcePlan',
  [ProjectDetailTabKeys.CustomerPortalConfiguration]:
    'customerportalconfiguration',
  [ProjectDetailTabKeys.Invoices]: 'invoice',
  [ProjectDetailTabKeys.Expense]: 'expense',
  [ProjectDetailTabKeys.ExpenseReport]: 'expenseReport',
  [ProjectDetailTabKeys.Sheets]: 'sheets',
};

export const tabParentChildMap = {
  [ProjectDetailTabKeys.ResourceManagement]: [
    ProjectDetailTabKeys.ResourcePlan,
    ProjectDetailTabKeys.ResourceRequest,
  ],
  [ProjectDetailTabKeys.FinancialsContainer]: [
    ProjectDetailTabKeys.Financials,
    ProjectDetailTabKeys.Timesheet,
    ProjectDetailTabKeys.Expense,
    ProjectDetailTabKeys.ExpenseReport,
    ProjectDetailTabKeys.Invoices,
  ],
};

export const childrenTabToParentMapping = Object.entries(
  tabParentChildMap,
).reduce((acc, [key, value]) => {
  const values = Array.isArray(value) ? value : [value];

  return {
    ...acc,
    ...values.reduce(
      (valueAcc, v) => ({
        ...valueAcc,
        [v]: key as ProjectDetailTabKeys,
      }),
      {},
    ),
  };
}, {} as Record<string, ProjectDetailTabKeys>);

export const CHILDREN_TABS = Object.values(tabParentChildMap).flat();
